import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Handshake, Share2, Cpu, ArrowRight, CheckCircle2 } from "lucide-react";
import Navbar from "../components/homepage/Navbar";
import Footer from "../components/homepage/Footer";
import CTABanner from "../components/homepage/CTABanner";
import PageHero, { SectionHeader } from "../components/shared/PageHero";

const TIERS = [
  {
    icon: Handshake,
    title: "Agency Partners",
    desc: "For digital, design and dev agencies that want to offer ASO without building a team.",
    perks: [
      "White-labeled ASO delivery",
      "Co-branded reporting",
      "25% recurring revenue share",
      "Dedicated partner manager",
    ],
  },
  {
    icon: Share2,
    title: "Affiliate Partners",
    desc: "For creators, consultants and operators who refer app founders to Lumotis.",
    perks: [
      "15% commission for 12 months",
      "Co-marketing opportunities",
      "Affiliate dashboard + tracking",
      "Early access to playbooks",
    ],
  },
  {
    icon: Cpu,
    title: "Technology Partners",
    desc: "For app analytics, MMP, CRM and monetization platforms building integrations.",
    perks: [
      "Joint solution briefs",
      "Integration-led case studies",
      "Event & webinar co-marketing",
      "Shared customer success",
    ],
  },
];

export default function PartnersPage() {
  return (
    <>
      <Helmet>
        <title>Lumotis Partner Program — Agencies, Affiliates, Technology</title>
        <meta
          name="description"
          content="Join the Lumotis Partner Program. Agency partners, affiliate partners and technology partners — grow together with India's #1 ASO agency."
        />
        <link rel="canonical" href="https://lumotis.com/partners" />
      </Helmet>
      <Navbar />
      <main data-testid="partners-page">
        <PageHero
          testid="partners-hero"
          eyebrow="Partner Program"
          breadcrumb={[{ label: "Home", href: "/" }, { label: "Partners" }]}
          title="Grow with the"
          titleAccent="#1 ASO agency"
          subtitle="Three tracks — Agency, Affiliate, Technology. Built for partners who want to deliver ASO, refer app founders, or integrate with the Lumotis growth stack."
          primaryCta={{ to: "/contact", label: "Become a partner", testid: "partners-cta-primary" }}
        />

        <section className="py-20 md:py-28 bg-zinc-50 border-y border-zinc-200">
          <div className="container">
            <SectionHeader
              eyebrow="01 — Tracks"
              title="Pick the track that fits."
              description="Clear economics. Clear ownership. No red tape."
            />
            <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
              {TIERS.map((t, idx) => {
                const Icon = t.icon;
                const highlighted = idx === 1;
                return (
                  <article
                    key={t.title}
                    className={`p-8 md:p-10 flex flex-col ${
                      highlighted
                        ? "bg-charcoal text-white border border-charcoal shadow-xl shadow-electric/20 rounded-2xl"
                        : "bg-white border border-zinc-200"
                    }`}
                    data-testid={`partner-tier-${idx}`}
                  >
                    <span
                      className={`w-11 h-11 grid place-items-center bg-electric text-white mb-6`}
                    >
                      <Icon className="w-5 h-5" strokeWidth={2.2} />
                    </span>
                    <h3 className="font-display text-2xl font-bold">{t.title}</h3>
                    <p
                      className={`mt-3 text-[15px] leading-relaxed ${highlighted ? "text-zinc-300" : "text-zinc-600"}`}
                    >
                      {t.desc}
                    </p>
                    <ul className="mt-6 space-y-3 flex-1">
                      {t.perks.map((p) => (
                        <li key={p} className="flex items-start gap-3 text-sm">
                          <CheckCircle2
                            className="w-4 h-4 mt-0.5 text-electric flex-shrink-0"
                            strokeWidth={2.5}
                          />
                          <span
                            className={
                              highlighted ? "text-zinc-200" : "text-zinc-700"
                            }
                          >
                            {p}
                          </span>
                        </li>
                      ))}
                    </ul>
                    <Link
                      to="/contact"
                      data-testid={`partner-cta-${idx}`}
                      className={`mt-10 inline-flex items-center justify-center gap-2 px-5 py-3 font-semibold transition-all ${
                        highlighted
                          ? "bg-electric text-white hover:bg-electric-400"
                          : "bg-charcoal text-white hover:bg-electric"
                      }`}
                    >
                      Apply <ArrowRight className="w-4 h-4" />
                    </Link>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <CTABanner />
      </main>
      <Footer />
    </>
  );
}
